import { create } from 'zustand';

/**
 * Toast Store - Manages toast notifications (GRO-35)
 *
 * Types:
 * - success: Green checkmark
 * - error: Red X
 * - warning: Orange warning
 * - info: Blue info
 */

let toastId = 0;

export const useToastStore = create((set, get) => ({
  toasts: [],

  /**
   * Add a toast notification
   * @param {object} toast - Toast configuration
   * @param {string} toast.message - Message to display
   * @param {string} toast.type - Type: 'success' | 'error' | 'warning' | 'info'
   * @param {number} toast.duration - Duration in ms (default: 4000, 0 for persistent)
   * @param {string} toast.action - Optional action button text
   * @param {function} toast.onAction - Optional action callback
   * @returns {number} - Toast ID
   */
  addToast: ({ message, type = 'info', duration = 4000, action = null, onAction = null }) => {
    const id = ++toastId;
    const toast = { id, message, type, action, onAction, createdAt: Date.now() };

    set((state) => ({
      toasts: [...state.toasts, toast],
    }));

    // Auto-dismiss after duration (unless duration is 0)
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }

    return id;
  },

  /**
   * Remove a toast by ID
   * @param {number} id - Toast ID
   */
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  /**
   * Clear all toasts
   */
  clearAll: () => {
    set({ toasts: [] });
  },

  // Convenience methods
  success: (message, options = {}) =>
    get().addToast({ message, type: 'success', ...options }),

  error: (message, options = {}) =>
    get().addToast({ message, type: 'error', duration: 6000, ...options }),

  warning: (message, options = {}) =>
    get().addToast({ message, type: 'warning', ...options }),

  info: (message, options = {}) =>
    get().addToast({ message, type: 'info', ...options }),
}));

export default useToastStore;
